// QuickLogDialog — log a workout that was NEVER programmed (e.g., "I did Murph
// in the park on Saturday"). Pick a date, pick a workout from the CrossFit
// benchmark library OR build one from scratch, then enter your result.
//
// Saved logs are not bound to a `program_id` — the backend accepts athlete_id
// + logged_at directly so freestyle/backdated entries land in the History
// timeline and PR aggregations like any other workout.

import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, Trash, Plus, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError, todayISO } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const BLOCK_TYPES = [
  { value: "strength", label: "Strength / Set" },
  { value: "metcon",   label: "For Time (MetCon)" },
  { value: "amrap",    label: "AMRAP" },
  { value: "emom",     label: "EMOM" },
  { value: "tabata",   label: "Tabata" },
  { value: "ladder",   label: "Ladder" },
  { value: "intervals", label: "Intervals" },
  { value: "self_defense", label: "Self Defense" },
];

const empty = () => ({ name: "", reps: "", weight: "", weight_unit: "lb", time_seconds: "", distance: "", distance_unit: "m", height: "", height_unit: "in", calories: "", rpe: "", notes: "" });

export default function QuickLogDialog({ open, onOpenChange, onSaved }) {
  const { user } = useAuth();
  const [date, setDate] = useState(todayISO());
  const [type, setType] = useState("metcon");
  const [title, setTitle] = useState("");
  // For-time / cap stopwatch
  const [minutes, setMinutes] = useState("");
  const [seconds, setSeconds] = useState("");
  // AMRAP / EMOM
  const [rounds, setRounds] = useState("");
  const [extraReps, setExtraReps] = useState("");
  // Free notes / RPE on the parent log
  const [rpe, setRpe] = useState("");
  const [notes, setNotes] = useState("");
  // Optional per-movement child logs
  const [mvRows, setMvRows] = useState([]);
  // WOD library + autocomplete
  const [wodLib, setWodLib] = useState([]);
  const [wodQuery, setWodQuery] = useState("");
  const [busy, setBusy] = useState(false);

  // Load WOD library when dialog opens (cached after first open).
  useEffect(() => {
    if (!open || wodLib.length) return;
    api.get("/wod-library").then((r) => setWodLib(r.data || [])).catch(() => { /* non-fatal */ });
  }, [open, wodLib.length]);

  const filteredWods = useMemo(() => {
    const q = wodQuery.trim().toLowerCase();
    if (!q) return [];
    return wodLib
      .filter((w) => w.name.toLowerCase().includes(q) || (w.category || "").toLowerCase().includes(q))
      .slice(0, 6);
  }, [wodLib, wodQuery]);

  const reset = () => {
    setDate(todayISO());
    setType("metcon");
    setTitle("");
    setMinutes(""); setSeconds("");
    setRounds(""); setExtraReps("");
    setRpe(""); setNotes("");
    setMvRows([]);
    setWodQuery("");
  };

  const pickWod = (w) => {
    setTitle(w.name);
    setType(w.type || "metcon");
    setWodQuery("");
    // Pre-fill movements from the WOD blueprint so the user just enters their result.
    setMvRows((w.movements || []).map((m) => ({
      ...empty(),
      name: m.name,
      reps: m.reps ?? "",
      weight: m.weight ?? "",
      weight_unit: m.weight_unit || "lb",
      distance: m.distance_meters ?? "",
      distance_unit: m.distance_unit || "m",
      height: m.height_inches ?? "",
      height_unit: m.height_unit || "in",
      calories: m.calories ?? "",
    })));
  };

  const addRow = () => setMvRows((r) => [...r, empty()]);
  const updRow = (i, patch) => setMvRows((rs) => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const delRow = (i) => setMvRows((rs) => rs.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Give your workout a title"); return; }
    setBusy(true);
    try {
      const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
      const totalSec = (Number(minutes) || 0) * 60 + (Number(seconds) || 0);
      // Append a timestamp to the date so this lands as logged_at at noon-ish of that day.
      const loggedAt = `${date}T12:00:00.000Z`;
      // Parent log captures the block-level result (time / rounds / rpe / notes).
      const parent = await api.post("/workout-logs", {
        athlete_id: user.id,
        logged_at: loggedAt,
        day_of_week: new Date(date).getDay(),
        set_number: 1,
        block_type: type,
        block_title: title.trim(),
        block_id: `freestyle-${Date.now()}`,
        total_time_seconds: totalSec > 0 ? totalSec : null,
        rounds_completed: num(rounds),
        extra_reps: num(extraReps),
        rpe: num(rpe),
        notes: notes.trim() || null,
      });
      // Per-movement child logs (optional).
      const results = await Promise.allSettled(mvRows
        .filter((m) => (m.name || "").trim())
        .map((m, idx) => api.post("/workout-logs", {
          athlete_id: user.id,
          logged_at: loggedAt,
          day_of_week: new Date(date).getDay(),
          set_number: idx + 1,
          block_id: parent.data?.block_id || null,
          block_type: type,
          block_title: title.trim(),
          movement_name: m.name.trim(),
          reps: num(m.reps),
          weight: num(m.weight),
          weight_unit: m.weight_unit,
          time_seconds: num(m.time_seconds),
          distance_meters: num(m.distance),
          distance_unit: m.distance_unit,
          height_inches: num(m.height),
          height_unit: m.height_unit,
          calories: num(m.calories),
          rpe: num(m.rpe),
          notes: m.notes || null,
        })));
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed > 0) {
        toast.warning(`Workout saved, but ${failed} movement detail${failed > 1 ? "s" : ""} failed to log.`);
      } else {
        toast.success(`Logged "${title.trim()}" on ${date}`);
      }
      reset();
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="quick-log-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Log a past workout</DialogTitle>
          <DialogDescription>
            Any workout, any date — Murph, an open-gym MetCon, a long bike ride. It'll land in your History and count toward your PRs.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5" data-testid="quick-log-form">
          {/* Date + Type */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="hs-label">Date</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                max={todayISO()}
                className="mt-2 h-11 rounded-xl font-data"
                required
                data-testid="quick-log-date"
              />
            </div>
            <div>
              <Label className="hs-label">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="quick-log-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BLOCK_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Title with WOD autocomplete */}
          <div className="relative">
            <Label className="hs-label">Workout name</Label>
            <div className="relative mt-2">
              <Input
                value={title}
                onChange={(e) => { setTitle(e.target.value); setWodQuery(e.target.value); }}
                placeholder="e.g. Murph, Fran, Open Gym AMRAP"
                className="h-11 rounded-xl pl-10"
                required
                data-testid="quick-log-title"
              />
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3" />
            </div>
            {filteredWods.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-surface border border-line rounded-xl shadow-lift overflow-hidden" data-testid="quick-log-wod-suggestions">
                {filteredWods.map((w) => (
                  <button
                    type="button"
                    key={w.slug}
                    onClick={() => pickWod(w)}
                    className="w-full text-left px-3 py-2 hover:bg-muted2 flex items-center justify-between gap-2"
                    data-testid={`quick-log-wod-${w.slug}`}
                  >
                    <div className="min-w-0">
                      <div className="font-medium truncate">{w.name}</div>
                      <div className="text-xs text-ink-3 truncate">{w.category || w.type}</div>
                    </div>
                    <Plus size={14} className="text-moss shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Result row — time for For Time / cap stopwatch; rounds for AMRAP/EMOM */}
          {(type === "metcon" || type === "ladder" || type === "intervals") && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="hs-label">Time — minutes</Label>
                <Input type="number" min="0" value={minutes} onChange={(e) => setMinutes(e.target.value)} className="mt-2 h-11 rounded-xl font-data" data-testid="quick-log-minutes" />
              </div>
              <div>
                <Label className="hs-label">Time — seconds</Label>
                <Input type="number" min="0" max="59" value={seconds} onChange={(e) => setSeconds(e.target.value)} className="mt-2 h-11 rounded-xl font-data" data-testid="quick-log-seconds" />
              </div>
            </div>
          )}
          {(type === "amrap" || type === "emom" || type === "tabata") && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="hs-label">Rounds completed</Label>
                <Input type="number" min="0" value={rounds} onChange={(e) => setRounds(e.target.value)} className="mt-2 h-11 rounded-xl font-data" data-testid="quick-log-rounds" />
              </div>
              <div>
                <Label className="hs-label">+ Extra reps</Label>
                <Input type="number" min="0" value={extraReps} onChange={(e) => setExtraReps(e.target.value)} className="mt-2 h-11 rounded-xl font-data" data-testid="quick-log-extra-reps" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="hs-label">RPE <span className="text-ink-3 normal-case">(optional)</span></Label>
              <Input type="number" step="0.5" min="0" max="10" value={rpe} onChange={(e) => setRpe(e.target.value)} className="mt-2 h-11 rounded-xl font-data" />
            </div>
          </div>

          {/* Per-movement details (optional) */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="hs-label">Movements <span className="text-ink-3 normal-case">(optional)</span></Label>
              <Button type="button" variant="ghost" size="sm" onClick={addRow} className="rounded-lg h-7 text-xs" data-testid="quick-log-add-movement">
                <Plus size={12} className="mr-1" /> Add movement
              </Button>
            </div>
            {mvRows.length > 0 && (
              <div className="mt-2 space-y-2">
                {mvRows.map((m, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center rounded-xl bg-muted2/40 p-2" data-testid={`quick-log-movement-row-${i}`}>
                    <Input className="col-span-4 h-9 rounded-lg text-sm" placeholder="Movement" value={m.name} onChange={(e) => updRow(i, { name: e.target.value })} data-testid={`quick-log-movement-name-${i}`} />
                    <Input className="col-span-2 h-9 rounded-lg text-sm font-data" type="number" placeholder="Reps" value={m.reps} onChange={(e) => updRow(i, { reps: e.target.value })} />
                    <Input
                      className="col-span-2 h-9 rounded-lg text-sm font-data"
                      type="number"
                      step={m.weight_unit === "level" ? "1" : "0.5"}
                      min={m.weight_unit === "level" ? "0" : undefined}
                      placeholder="Weight"
                      value={m.weight}
                      onChange={(e) => updRow(i, { weight: e.target.value })}
                    />
                    <Select
                      value={m.weight_unit}
                      onValueChange={(v) => updRow(i, {
                        weight_unit: v,
                        // Cross-domain switch clears the number so it can't
                        // be silently coerced (level and lb/kg aren't the
                        // same measurement).
                        weight: (v === "level" || m.weight_unit === "level") && v !== m.weight_unit ? "" : m.weight,
                      })}
                    >
                      <SelectTrigger className="col-span-2 h-9 rounded-lg text-sm font-data" data-testid={`quick-log-unit-trigger-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lb">lb</SelectItem>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="level">L (level)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button type="button" size="icon" variant="ghost" onClick={() => delRow(i)} className="col-span-2 rounded-lg h-9 text-error hover:bg-red-50">
                      <Trash size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="hs-label">Notes <span className="text-ink-3 normal-case">(optional)</span></Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="How did it feel, what you'd do differently…" className="mt-2 rounded-xl" />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" className="rounded-xl" onClick={() => { reset(); onOpenChange(false); }}>Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-xl bg-moss hover:bg-moss-hover text-white hover:text-white" data-testid="quick-log-submit">
              <CheckCircle size={14} className="mr-1.5" weight="bold" />
              {busy ? "Saving…" : "Save workout"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
